<script setup lang="ts">
import { useElementVisibility, useDebounceFn } from '@vueuse/core'

const { t } = useI18n()

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  class: {
    type: [String, Object, Array],
    default: undefined,
  },
})

const config = computed(() => ({
  wrapper:
    'sticky top-[--header-height] bg-background/75 backdrop-blur -mx-4 sm:-mx-6 px-4 sm:px-6 lg:px-4 lg:-mx-4 overflow-y-auto max-h-[calc(100vh-var(--header-height))]',
  container: {
    base: 'py-3 lg:py-8 space-y-3',
    empty: 'lg:py-8 space-y-3',
  },
  button: {
    base: 'flex items-center gap-1.5 lg:cursor-text lg:select-text w-full group',
    label: 'font-semibold text-sm/6 truncate',
    trailingIcon: {
      name: 'i-tabler-chevron-down',
      base: 'w-5 h-5 ms-auto transform transition-transform duration-200 flex-shrink-0 mr-1.5',
      active: 'text-gray-700 dark:text-gray-200',
      inactive:
        'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 -rotate-90',
    },
  },
  links: {
    wrapper: 'space-y-1',
    base: 'block text-sm leading-normal',
    link: 'block',
    active: 'text-primary-500 font-medium',
    inactive:
      'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
    depth: 'ml-3',
  },
}))

const { ui, attrs } = useUI(
  'content.toc',
  ref<Record<string, any>>({}),
  config,
  toRef(props, 'class')
)

const open = ref(false)

const slugify = (str: string) => {
  // Check if the string starts with a number
  const numberMatch = str.match(/^(\d+(?:\.\d+)?)\.\s*(.+)$/)

  if (numberMatch) {
    // If it's a numbered heading, format as "_number-text"
    const [, number, text] = numberMatch
    const cleanNumber = number.replace('.', '')
    const cleanText = text
      .toLowerCase()
      .trim()
      .replace(/[\s\-_]+/g, '-')
      .replace(/[^\w\-\u4e00-\u9fff]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+|-+$/g, '')

    return `_${cleanNumber}-${cleanText}`
  }

  // For non-numbered headings, use the regular slugify
  return str
    .toLowerCase()
    .trim()
    .replace(/[\s\-_]+/g, '-')
    .replace(/[^\w\-\u4e00-\u9fff]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const toc = computed(() => {
  // Match both regular and numbered headings
  const headingPattern = /^(#{2,3})\s*(.+)$/gm
  const matches = Array.from(props.content.matchAll(headingPattern))

  return matches.map((match) => {
    const [, hashes, fullText] = match
    const text = fullText.trim()
    // Remove any existing HTML tags for clean text
    const cleanText = text.replace(/<[^>]*>/g, '')

    return {
      level: hashes.length,
      text: cleanText, // Keep the original text with numbers
      id: slugify(cleanText), // Create ID with number prefix for numbered headings
    }
  })
})

const nuxtApp = useNuxtApp()
const route = useRoute()
const activeHeadings = ref<string[]>([])

const updateHeadings = () => {
  const headings = [...document.querySelectorAll('h2[id], h3[id]')]

  const visibleHeadings = headings.filter((heading) => {
    const rect = heading.getBoundingClientRect()
    return rect.top >= 0 && rect.top <= window.innerHeight * 0.5
  })

  if (visibleHeadings.length > 0) {
    activeHeadings.value = visibleHeadings.map((heading) => heading.id)
  }
}

const contentRef = ref(null)
const isContentVisible = useElementVisibility(contentRef)

// Debounced scroll handler
const debouncedUpdateHeadings = useDebounceFn(updateHeadings, 100)

onMounted(() => {
  // Initial update
  nextTick(updateHeadings)

  // Add scroll listener
  window.addEventListener('scroll', debouncedUpdateHeadings, { passive: true })

  // Check for hash in URL and scroll to it
  if (route.hash) {
    const id = route.hash.slice(1)
    nextTick(() => {
      scrollToHeading(id)
    })
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', debouncedUpdateHeadings)
})

// Watch for route changes
watch(
  () => route.path,
  () => {
    nextTick(updateHeadings)
  }
)

const isActive = (id: string) => {
  return activeHeadings.value.includes(id)
}

const scrollToHeading = (id: string) => {
  const element = document.getElementById(id)
  if (element) {
    const offset = 80 // Adjust this value based on your header height
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - offset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })

    // Preserve existing history state when updating URL
    const url = `${window.location.pathname}${window.location.search}#${id}`
    history.replaceState({ ...history.state }, '', url)

    // Update active headings
    activeHeadings.value = [id]
    open.value = false
  }
}
</script>

<template>
  <nav v-bind="attrs" ref="contentRef" :class="ui.wrapper">
    <div :class="[toc.length ? ui.container.base : ui.container.empty]">
      <slot name="top"></slot>

      <button
        v-if="toc.length"
        :class="ui.button.base"
        tabindex="-1"
        @click="open = !open"
      >
        <span :class="ui.button.label">
          {{ t('articles.tableOfContents') }}
        </span>

        <UIcon
          :name="ui.button.trailingIcon.name"
          class="lg:!hidden"
          :class="[
            ui.button.trailingIcon.base,
            open
              ? ui.button.trailingIcon.active
              : ui.button.trailingIcon.inactive,
          ]"
        />
      </button>

      <ul :class="[ui.links.wrapper, open ? 'lg:block' : 'hidden lg:block']">
        <li
          v-for="link in toc"
          :key="link.id"
          :class="[ui.links.base, link.level === 3 && ui.links.depth]"
        >
          <a
            :href="`#${link.id}`"
            :class="[
              ui.links.link,
              isActive(link.id) ? ui.links.active : ui.links.inactive,
            ]"
            @click.prevent="scrollToHeading(link.id)"
          >
            {{ link.text }}
          </a>
        </li>
      </ul>

      <slot name="bottom"></slot>
    </div>
  </nav>
</template>
